/**
 * Bundle / volume offer picker for the product page.
 *
 * Each option carries the quantity that gets added to the cart plus the pricing the
 * customer is shown. Selecting one keeps the product form's quantity in sync, refreshes
 * the option prices and (optionally) prints the bundle total on the add-to-cart button.
 *
 * The money saved has to be granted by a Shopify discount — either an automatic discount
 * or a discount code set on the option — because a theme cannot change line prices itself.
 */
if (!customElements.get('bundle-options')) {
  customElements.define(
    'bundle-options',
    class BundleOptions extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
        this.unitCompareAt = parseInt(this.dataset.unitCompareAt, 10) || 0;
        this.updateButton = this.dataset.updateButton === 'true';
        this.addToCartText = this.dataset.addToCartText || 'Add to cart';

        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.onVariantChange(event.data.variant);
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          this.applyDiscountCode();
        });

        this.syncSelection({ rerenderPrices: false });
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      get inputs() {
        return Array.from(this.querySelectorAll('.bundle-option__input'));
      }

      get selectedInput() {
        return this.querySelector('.bundle-option__input:checked') || this.inputs[0];
      }

      get quantityInput() {
        // The quantity selector block owns the input when it is visible; otherwise the
        // snippet renders its own hidden one.
        return (
          document.getElementById(`Quantity-${this.sectionId}`) || this.querySelector('[data-bundle-quantity]')
        );
      }

      get submitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      onChange(event) {
        if (!event.target.classList.contains('bundle-option__input')) return;
        this.syncSelection({ rerenderPrices: false });
      }

      onVariantChange(variant) {
        if (!variant) return;

        this.unitPrice = variant.price || 0;
        this.unitCompareAt = Math.max(variant.compare_at_price || 0, variant.price || 0);

        this.syncSelection({ rerenderPrices: true });
      }

      /**
       * Pushes the checked option out to the rest of the product form: the highlight, the
       * quantity, the line item property and the add-to-cart button label.
       */
      syncSelection({ rerenderPrices }) {
        const selected = this.selectedInput;
        if (!selected) return;

        if (rerenderPrices) this.renderPrices();

        this.inputs.forEach((input) => {
          input.closest('.bundle-option')?.classList.toggle('bundle-option--selected', input === selected);
        });

        const quantityInput = this.quantityInput;
        if (quantityInput) {
          quantityInput.value = selected.dataset.quantity;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const property = this.querySelector('[data-bundle-property]');
        if (property) property.value = selected.dataset.label || '';

        this.renderSubmitButton();
      }

      /** Recalculates every option's totals against the currently selected variant. */
      renderPrices() {
        this.inputs.forEach((input) => {
          const { price, compareAt } = this.totalsFor(input);
          const option = input.closest('.bundle-option');
          if (!option) return;

          const priceElement = option.querySelector('[data-bundle-price]');
          if (priceElement) priceElement.innerHTML = this.formatMoney(price);

          const compareElement = option.querySelector('[data-bundle-compare]');
          if (compareElement) {
            compareElement.innerHTML = this.formatMoney(compareAt);
            compareElement.classList.toggle('hidden', compareAt <= price);
          }
        });
      }

      renderSubmitButton() {
        if (!this.updateButton) return;

        const button = this.submitButton;
        const selected = this.selectedInput;
        if (!button || !selected) return;

        // Matches Dawn's own product-form.js: the first span is the button label.
        const label = button.querySelector('span');
        if (!label) return;

        // Leave sold out / unavailable messaging alone.
        if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

        const { price, compareAt } = this.totalsFor(selected);
        const parts = [
          `<span class="bundle-atc__label">${this.addToCartText}</span>`,
          '<span class="bundle-atc__separator" aria-hidden="true">•</span>',
          `<span class="bundle-atc__price">${this.formatMoney(price)}</span>`,
        ];

        if (compareAt > price) {
          parts.push(`<s class="bundle-atc__compare">${this.formatMoney(compareAt)}</s>`);
        }

        label.classList.add('bundle-atc');
        label.innerHTML = parts.join(' ');
      }

      totalsFor(input) {
        const quantity = parseInt(input.dataset.quantity, 10) || 1;
        const fixedPrice = parseInt(input.dataset.fixedPrice, 10) || 0;
        const discountPercent = parseFloat(input.dataset.discountPercent) || 0;

        const price = fixedPrice > 0 ? fixedPrice : Math.round(this.unitPrice * quantity * (1 - discountPercent / 100));

        return { price, compareAt: this.unitCompareAt * quantity };
      }

      /**
       * Drops the option's discount code cookie after a successful add so the saving the
       * customer was shown is waiting for them at checkout.
       */
      applyDiscountCode() {
        const code = this.selectedInput?.dataset.discountCode;
        if (!code) return;

        const root = window.Shopify?.routes?.root || '/';
        fetch(`${root}discount/${encodeURIComponent(code)}`, { method: 'GET', credentials: 'same-origin' }).catch(
          (error) => console.error('Unable to apply bundle discount code', error)
        );
      }

      formatMoney(cents) {
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

        const withDelimiters = (precision, thousands = ',', decimal = '.') => {
          if (isNaN(cents) || cents === null) return '0';

          const fixed = (cents / 100.0).toFixed(precision);
          const [whole, fraction] = fixed.split('.');
          const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

          return fraction ? grouped + decimal + fraction : grouped;
        };

        const match = this.moneyFormat.match(placeholderRegex);
        let value;

        switch (match && match[1]) {
          case 'amount_no_decimals':
            value = withDelimiters(0);
            break;
          case 'amount_with_comma_separator':
            value = withDelimiters(2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withDelimiters(0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = withDelimiters(2, "'");
            break;
          case 'amount_no_decimals_with_space_separator':
            value = withDelimiters(0, ' ');
            break;
          case 'amount_with_space_separator':
            value = withDelimiters(2, ' ', ',');
            break;
          case 'amount_with_period_and_space_separator':
            value = withDelimiters(2, ' ', '.');
            break;
          default:
            value = withDelimiters(2);
        }

        return this.moneyFormat.replace(placeholderRegex, value);
      }
    }
  );
}
